import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import { getUserInfo } from "../../../api/axios";
import {
  PDP_STATUS,
  approvePdp,
  completePdp,
  getActiveLmsLinks,
  getMyPdpTasks,
  getPdpTask,
  getTeamPdpTasks,
  pdpStatusLabel,
  savePdpTask,
  submitPdpForApproval,
  addPdpComment,
} from "./appraisalApi";
import {
  AppraisalGuideButton,
  AppraisalHelpModal,
  AppraisalSectionHeader,
} from "./AppraisalHelp";
import { getAppraisalRoleId } from "./appraisalAccess";
import { AppraisalEmployeeCell, pickEmployeeFields } from "./AppraisalEmployeeCell";
import { APPRAISAL_LIMITS, clampLen, validateFields } from "./appraisalValidation";
import { AppraisalCharCount } from "./AppraisalFormFields";
import "./Appraisal.css";

const emptyForm = () => ({
  id: "",
  activityName: "",
  description: "",
  learningMethod: "",
  startDate: "",
  completionDate: "",
  rateProgress: 0,
});

const getCurrentUserId = () => {
  const userInfo = getUserInfo();
  if (!userInfo) return "";
  return String(
    userInfo.selfUserId ||
      userInfo.id ||
      userInfo.userId ||
      userInfo.Id ||
      userInfo.UserId ||
      ""
  ).trim();
};

const normalizeTask = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const employee = pickEmployeeFields(raw);
  return {
    ...raw,
    id: raw.id ?? raw.Id,
    userId: raw.userId ?? raw.UserId ?? "",
    ...employee,
    managerId: raw.managerId ?? raw.ManagerId ?? "",
    status: Number(raw.status ?? raw.Status ?? 0),
    activityName: raw.activityName ?? raw.ActivityName ?? "",
    description: raw.description ?? raw.Description ?? "",
    learningMethod: raw.learningMethod ?? raw.LearningMethod ?? "",
    startDate: raw.startDate ?? raw.StartDate,
    completionDate: raw.completionDate ?? raw.CompletionDate,
    rateProgress: raw.rateProgress ?? raw.RateProgress ?? 0,
    comments: raw.comments ?? raw.Comments ?? [],
  };
};

const teamPdpActionLabel = (status) => {
  switch (Number(status)) {
    case 1:
      return "Review";
    case 2:
      return "View";
    case 3:
      return "View";
    default:
      return "Open";
  }
};

/** mode: "mine" | "team" | "detail" */
const AppraisalPdpPage = ({ mode = "mine" }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [rows, setRows] = useState([]);
  const [task, setTask] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [showForm, setShowForm] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [lmsLinks, setLmsLinks] = useState([]);
  const [helpKey, setHelpKey] = useState(null);
  const [savingPdp, setSavingPdp] = useState(false);
  const [teamDetailId, setTeamDetailId] = useState(null);

  const isTeam = mode === "team";
  const detailId = isTeam ? teamDetailId : id || null;
  const showingDetail = Boolean(detailId) || mode === "detail";
  const fromTeam =
    isTeam ||
    location.state?.from === "team" ||
    new URLSearchParams(location.search || "").get("from") === "team";

  const title = useMemo(() => {
    if (isTeam) return t("app.appraisal.teamPdpTitle", { defaultValue: "Team PDP" });
    if (showingDetail) return t("app.appraisal.pdpDetailTitle", { defaultValue: "PDP Detail" });
    return t("app.appraisal.myPdpTitle", { defaultValue: "My Development Plan" });
  }, [showingDetail, isTeam, t]);

  const actor = useMemo(() => {
    const userId = getCurrentUserId();
    const roleId = getAppraisalRoleId();
    const isOwner = Boolean(task?.userId && String(task.userId) === userId);
    const isAssignedManager = Boolean(task?.managerId && String(task.managerId) === userId);
    const isAdmin = roleId === 1 || roleId === 2;
    const canManage = isAssignedManager || (isAdmin && !isOwner);
    return { isOwner, canManage };
  }, [task]);

  const status = Number(task?.status ?? -1);
  const canEdit = Boolean(task) && actor.isOwner && status === PDP_STATUS.Draft;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      if (detailId) {
        const detail = await getPdpTask(detailId);
        const normalized = normalizeTask(detail);
        setTask(normalized);
        setForm({
          id: normalized.id,
          activityName: normalized.activityName || "",
          description: normalized.description || "",
          learningMethod: normalized.learningMethod || "",
          startDate: normalized.startDate ? String(normalized.startDate).slice(0, 10) : "",
          completionDate: normalized.completionDate
            ? String(normalized.completionDate).slice(0, 10)
            : "",
          rateProgress: normalized.rateProgress || 0,
        });
      } else {
        const [list, links] = await Promise.all([
          isTeam ? getTeamPdpTasks() : getMyPdpTasks(),
          !isTeam ? getActiveLmsLinks().catch(() => []) : Promise.resolve([]),
        ]);
        setRows(Array.isArray(list) ? list.map(normalizeTask) : []);
        setLmsLinks(
          Array.isArray(links)
            ? links.map((raw) => ({
                id: raw.id ?? raw.Id,
                name: raw.name ?? raw.Name ?? "",
                description: raw.description ?? raw.Description ?? "",
                buttonText: raw.buttonText ?? raw.ButtonText ?? "Open course",
                buttonLink: raw.buttonLink ?? raw.ButtonLink ?? "",
                isNewTab: raw.isNewTab ?? raw.IsNewTab,
              }))
            : []
        );
      }
    } catch (e) {
      setError(e?.message || "Failed to load PDP");
    } finally {
      setLoading(false);
    }
  }, [detailId, isTeam]);

  useEffect(() => {
    load();
  }, [load]);

  const onSave = async (e) => {
    e.preventDefault();
    if (savingPdp) return;
    setMessage("");
    setError("");
    const validationError = validateFields([
      {
        value: form.activityName,
        required: true,
        max: APPRAISAL_LIMITS.headline,
        label: "Activity name",
      },
      { value: form.description, max: APPRAISAL_LIMITS.longText, label: "Description" },
      {
        value: form.learningMethod,
        max: APPRAISAL_LIMITS.learningMethod,
        label: "Learning method",
      },
    ]);
    if (validationError) {
      setError(validationError);
      return;
    }
    setSavingPdp(true);
    try {
      const saved = await savePdpTask({
        ...form,
        startDate: form.startDate || null,
        completionDate: form.completionDate || null,
      });
      setMessage(
        t("app.appraisal.pdp.saved", { defaultValue: "Development activity saved." })
      );
      setShowForm(false);
      setForm(emptyForm());
      if (saved?.id && !showingDetail) {
        history.push({
          pathname: `/appraisal/pdp/${saved.id}`,
          state: fromTeam ? { from: "team" } : undefined,
        });
      } else await load();
    } catch (err) {
      setError(err?.message || "Failed to save PDP");
    } finally {
      setSavingPdp(false);
    }
  };

  const openNewActivityForm = () => {
    setForm(emptyForm());
    setShowForm(true);
    setError("");
    setMessage("");
  };

  const runAction = async (fn, payload = {}) => {
    setMessage("");
    setError("");
    try {
      await fn({ pdTaskId: task.id, ...payload });
      setMessage("Updated.");
      setCommentText("");
      await load();
    } catch (err) {
      setError(err?.message || "Action failed");
    }
  };

  const openTask = (taskId) => {
    if (isTeam) {
      setTeamDetailId(taskId);
      setError("");
      setMessage("");
      return;
    }
    history.push(`/appraisal/pdp/${taskId}`);
  };

  return (
    <div className={`appraisal-page${!isTeam && !showingDetail ? " appraisal-my-pdp-page" : ""}`}>
      <CommonPageHeader
        icon={<i className="bi bi-rocket-takeoff" />}
        title={title}
        description={
          showingDetail
            ? task?.activityName || ""
            : isTeam
              ? t("app.appraisal.teamPdpSubtitle", {
                  defaultValue:
                    "Review development activities for your team — approve, coach, and track progress.",
                })
              : t("app.appraisal.pdpSubtitle", {
                  defaultValue:
                    "Plan learning activities with your manager — from draft through approval to completion.",
                })
        }
        rightContent={
          !showingDetail ? (
            <div className="appraisal-page-header-actions">
              <AppraisalGuideButton onClick={() => setHelpKey("pdp")} />
              {!isTeam ? (
                <button
                  type="button"
                  className="appraisal-btn primary appraisal-header-cta"
                  onClick={openNewActivityForm}
                >
                  <i className="bi bi-plus-lg" aria-hidden />
                  {t("app.appraisal.pdp.newActivity", {
                    defaultValue: "New development activity",
                  })}
                </button>
              ) : null}
            </div>
          ) : null
        }
      />
      {loading ? <div className="appraisal-muted">Loading…</div> : null}
      {error ? <div className="appraisal-error">{error}</div> : null}
      {message ? <div className="appraisal-success">{message}</div> : null}

      {!showingDetail && !isTeam && lmsLinks.length > 0 ? (
        <section className="appraisal-settings-panel appraisal-lms-panel">
          <div className="appraisal-settings-panel__head">
            <AppraisalSectionHeader
              icon="bi-mortarboard"
              title={t("app.appraisal.lmsCourses", { defaultValue: "Learning courses" })}
              helpKey="lms"
              onOpenHelp={setHelpKey}
              tone="lms"
            />
            <p className="appraisal-framework-hint">
              {t("app.appraisal.lms.pdpHint", {
                defaultValue:
                  "Company training catalogue — separate from your PDP activities. Open a course, then create a development activity if you want manager tracking.",
              })}
            </p>
          </div>
          <ul className="appraisal-lms-catalogue">
            {lmsLinks.map((link) => (
              <li key={link.id} className="appraisal-lms-course">
                <div className="appraisal-lms-course__body">
                  <h4 className="appraisal-lms-course__name">{link.name || "Course"}</h4>
                  {link.description ? (
                    <p className="appraisal-lms-course__desc">{link.description}</p>
                  ) : (
                    <p className="appraisal-lms-course__desc appraisal-muted">
                      {t("app.appraisal.lms.noDescription", {
                        defaultValue: "No description provided.",
                      })}
                    </p>
                  )}
                </div>
                <a
                  className="appraisal-btn primary"
                  href={link.buttonLink}
                  target={link.isNewTab === false ? undefined : "_blank"}
                  rel="noreferrer"
                >
                  {link.buttonText || "Open course"}
                </a>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {!showingDetail && !isTeam ? (
        <section className="appraisal-settings-panel appraisal-pdp-panel">
          <div className="appraisal-settings-panel__head appraisal-goals-panel__head">
            <div>
              <AppraisalSectionHeader
                icon="bi-rocket-takeoff"
                title={t("app.appraisal.myPdpActivities", {
                  defaultValue: "My development activities",
                })}
                helpKey="pdp"
                onOpenHelp={setHelpKey}
                tone="pdp"
              />
              <p className="appraisal-framework-hint">
                {t("app.appraisal.myPdpActivitiesHint", {
                  defaultValue:
                    "Tracked learning plans with your manager — Draft → Submit → Approve → Complete.",
                })}
              </p>
            </div>
            <div className="appraisal-goals-panel__stats">
              <div className="appraisal-goals-stat">
                <strong>{rows.length}</strong>
                <span>{t("app.appraisal.pdp.totalLabel", { defaultValue: "Total" })}</span>
              </div>
              <div className="appraisal-goals-stat">
                <strong>
                  {rows.filter((r) => Number(r.status) !== PDP_STATUS.Completed).length}
                </strong>
                <span>{t("app.appraisal.pdp.activeLabel", { defaultValue: "Active" })}</span>
              </div>
            </div>
          </div>

          {showForm ? (
            <form onSubmit={onSave} className="appraisal-framework-form appraisal-pdp-create">
              <div className="appraisal-pdp-create__intro">
                <div className="appraisal-pdp-create__icon" aria-hidden>
                  <i className="bi bi-journal-plus" />
                </div>
                <div>
                  <strong>
                    {t("app.appraisal.pdp.createTitle", {
                      defaultValue: "Create a development activity",
                    })}
                  </strong>
                  <p>
                    {t("app.appraisal.pdp.createHint", {
                      defaultValue:
                        "Describe one learning goal you will work on. Your manager can approve it, then you track progress until it is complete.",
                    })}
                  </p>
                </div>
              </div>

              <div className="appraisal-pdp-steps" aria-hidden>
                <span className="appraisal-pdp-steps__item is-current">
                  <em>1</em>
                  {t("app.appraisal.pdp.stepDraft", { defaultValue: "Draft" })}
                </span>
                <i className="bi bi-chevron-right" />
                <span className="appraisal-pdp-steps__item">
                  <em>2</em>
                  {t("app.appraisal.pdp.stepSubmit", { defaultValue: "Submit" })}
                </span>
                <i className="bi bi-chevron-right" />
                <span className="appraisal-pdp-steps__item">
                  <em>3</em>
                  {t("app.appraisal.pdp.stepApprove", { defaultValue: "Approve" })}
                </span>
                <i className="bi bi-chevron-right" />
                <span className="appraisal-pdp-steps__item">
                  <em>4</em>
                  {t("app.appraisal.pdp.stepComplete", { defaultValue: "Complete" })}
                </span>
              </div>

              <label className="appraisal-field">
                {t("app.appraisal.pdp.activityName", { defaultValue: "Activity name" })}
                <input
                  required
                  disabled={savingPdp}
                  maxLength={APPRAISAL_LIMITS.headline}
                  placeholder={t("app.appraisal.pdp.activityNamePlaceholder", {
                    defaultValue: "e.g. Complete system-design fundamentals course",
                  })}
                  value={form.activityName}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      activityName: clampLen(e.target.value, APPRAISAL_LIMITS.headline),
                    }))
                  }
                />
                <small className="appraisal-field-hint">
                  {t("app.appraisal.pdp.activityNameHint", {
                    defaultValue: "A short title your manager will recognise at a glance.",
                  })}
                </small>
                <AppraisalCharCount value={form.activityName} max={APPRAISAL_LIMITS.headline} />
              </label>

              <label className="appraisal-field">
                {t("app.appraisal.pdp.description", { defaultValue: "What will you do?" })}
                <textarea
                  rows={3}
                  disabled={savingPdp}
                  maxLength={APPRAISAL_LIMITS.longText}
                  placeholder={t("app.appraisal.pdp.descriptionPlaceholder", {
                    defaultValue:
                      "Outline the activity, expected outcome, and how it helps your role…",
                  })}
                  value={form.description}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      description: clampLen(e.target.value, APPRAISAL_LIMITS.longText),
                    }))
                  }
                />
                <AppraisalCharCount value={form.description} max={APPRAISAL_LIMITS.longText} />
              </label>

              <label className="appraisal-field">
                {t("app.appraisal.pdp.learningMethod", { defaultValue: "How will you learn?" })}
                <input
                  disabled={savingPdp}
                  maxLength={APPRAISAL_LIMITS.learningMethod}
                  placeholder={t("app.appraisal.pdp.learningMethodPlaceholder", {
                    defaultValue: "e.g. Online course, mentoring, on-the-job project",
                  })}
                  value={form.learningMethod}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      learningMethod: clampLen(e.target.value, APPRAISAL_LIMITS.learningMethod),
                    }))
                  }
                />
                <small className="appraisal-field-hint">
                  {t("app.appraisal.pdp.learningMethodHint", {
                    defaultValue: "Optional — course, coaching, reading, shadowing, etc.",
                  })}
                </small>
                <AppraisalCharCount
                  value={form.learningMethod}
                  max={APPRAISAL_LIMITS.learningMethod}
                />
              </label>

              <div className="appraisal-grid-2">
                <label className="appraisal-field">
                  {t("app.appraisal.pdp.startDate", { defaultValue: "Planned start" })}
                  <input
                    type="date"
                    disabled={savingPdp}
                    value={form.startDate}
                    onChange={(e) => setForm((p) => ({ ...p, startDate: e.target.value }))}
                  />
                </label>
                <label className="appraisal-field">
                  {t("app.appraisal.pdp.targetDate", { defaultValue: "Target completion" })}
                  <input
                    type="date"
                    disabled={savingPdp}
                    value={form.completionDate}
                    onChange={(e) => setForm((p) => ({ ...p, completionDate: e.target.value }))}
                  />
                </label>
              </div>

              <div className="appraisal-framework-form__actions">
                <button
                  type="button"
                  className="appraisal-btn"
                  disabled={savingPdp}
                  onClick={() => {
                    setShowForm(false);
                    setForm(emptyForm());
                  }}
                >
                  {t("app.common.cancel", { defaultValue: "Cancel" })}
                </button>
                <button type="submit" className="appraisal-btn primary" disabled={savingPdp}>
                  {savingPdp
                    ? t("app.common.saving", { defaultValue: "Saving…" })
                    : t("app.appraisal.pdp.saveActivity", {
                        defaultValue: "Save activity",
                      })}
                </button>
              </div>
            </form>
          ) : null}

          <div className="appraisal-framework-list-block">
            <div className="appraisal-framework-list-block__head">
              <span>
                {t("app.appraisal.pdp.listTitle", { defaultValue: "Your activities" })}
              </span>
              <span className="appraisal-framework-count">{rows.length}</span>
            </div>

            {!loading && rows.length === 0 ? (
              <div className="appraisal-framework-empty">
                <i className="bi bi-rocket-takeoff" aria-hidden />
                <p>
                  {t("app.appraisal.noPdp", {
                    defaultValue:
                      "No development activities yet. Create one to start planning with your manager.",
                  })}
                </p>
                {!showForm ? (
                  <button
                    type="button"
                    className="appraisal-btn primary"
                    onClick={openNewActivityForm}
                  >
                    <i className="bi bi-plus-lg" aria-hidden />
                    {t("app.appraisal.pdp.newActivity", {
                      defaultValue: "New development activity",
                    })}
                  </button>
                ) : null}
              </div>
            ) : null}

            {!loading && rows.length > 0 ? (
              <div className="appraisal-table-wrap appraisal-team-table-wrap appraisal-goals-table-wrap">
                <table className="appraisal-table appraisal-team-table">
                  <thead>
                    <tr>
                      <th>{t("app.appraisal.colActivity", { defaultValue: "Activity" })}</th>
                      <th>{t("app.appraisal.colProgress", { defaultValue: "Progress" })}</th>
                      <th>{t("app.appraisal.colStatus", { defaultValue: "Status" })}</th>
                      <th className="appraisal-team-col-actions">
                        {t("app.appraisal.colActions", { defaultValue: "Actions" })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id}>
                        <td>
                          <div className="appraisal-goal-headline">{r.activityName || "—"}</div>
                          {r.learningMethod ? (
                            <div className="appraisal-goal-objective-meta">{r.learningMethod}</div>
                          ) : null}
                        </td>
                        <td className="appraisal-team-col-date">{r.rateProgress || 0}%</td>
                        <td>
                          <span
                            className={`appraisal-status-pill appraisal-status-pill--pdp-${Number(r.status) || 0}`}
                          >
                            {pdpStatusLabel(r.status)}
                          </span>
                        </td>
                        <td className="appraisal-team-col-actions">
                          <button
                            type="button"
                            className="appraisal-btn primary appraisal-team-action"
                            onClick={() => openTask(r.id)}
                          >
                            <i className="bi bi-box-arrow-up-right" aria-hidden />
                            {t("app.appraisal.open", { defaultValue: "Open" })}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      {!showingDetail && isTeam ? (
        <>
          <section className="appraisal-card">
            <AppraisalSectionHeader
              icon="bi-rocket-takeoff"
              title={t("app.appraisal.teamPdpActivities", {
                defaultValue: "Team development activities",
              })}
              helpKey="pdp"
              onOpenHelp={setHelpKey}
              tone="pdp"
            />
            <p className="appraisal-framework-hint" style={{ marginBottom: 0 }}>
              {t("app.appraisal.teamPdpActivitiesHint", {
                defaultValue: "Review and approve PDP tasks for your reports.",
              })}
            </p>
          </section>

          {!loading && rows.length === 0 ? (
            <div className="appraisal-team-empty">
              <i className="bi bi-rocket-takeoff" aria-hidden />
              <p>
                {t("app.appraisal.noTeamPdp", {
                  defaultValue: "No team PDP tasks yet.",
                })}
              </p>
            </div>
          ) : null}

          {!loading && rows.length > 0 ? (
            <div className="appraisal-table-wrap appraisal-team-table-wrap">
              <table className="appraisal-table appraisal-team-table">
                <thead>
                  <tr>
                    <th className="appraisal-team-col-employee">
                      {t("app.appraisal.colEmployee", { defaultValue: "Employee" })}
                    </th>
                    <th>{t("app.appraisal.colActivity", { defaultValue: "Activity" })}</th>
                    <th>{t("app.appraisal.colProgress", { defaultValue: "Progress" })}</th>
                    <th>{t("app.appraisal.colStatus", { defaultValue: "Status" })}</th>
                    <th className="appraisal-team-col-actions">
                      {t("app.appraisal.colActions", { defaultValue: "Actions" })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td className="appraisal-team-col-employee">
                        <AppraisalEmployeeCell
                          name={r.userName}
                          designation={r.designation}
                          department={r.department}
                          officeName={r.officeName}
                          profileImageUrl={r.profileImageUrl}
                        />
                      </td>
                      <td>
                        <div className="appraisal-goal-headline">{r.activityName || "—"}</div>
                        {r.learningMethod ? (
                          <div className="appraisal-goal-objective-meta">{r.learningMethod}</div>
                        ) : null}
                      </td>
                      <td className="appraisal-team-col-date">{r.rateProgress || 0}%</td>
                      <td>
                        <span
                          className={`appraisal-status-pill appraisal-status-pill--pdp-${Number(r.status) || 0}`}
                        >
                          {pdpStatusLabel(r.status)}
                        </span>
                      </td>
                      <td className="appraisal-team-col-actions">
                        <button
                          type="button"
                          className="appraisal-btn primary appraisal-team-action"
                          onClick={() => openTask(r.id)}
                        >
                          <i className="bi bi-box-arrow-up-right" aria-hidden />
                          {t(`app.appraisal.teamPdpAction.${Number(r.status)}`, {
                            defaultValue: teamPdpActionLabel(r.status),
                          })}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      ) : null}

      {showingDetail && task && (
        <>
          <div className="appraisal-card appraisal-checkin-detail">
            {isTeam ? (
              <button
                type="button"
                className="appraisal-checkin-back"
                onClick={() => {
                  setTeamDetailId(null);
                  setTask(null);
                  setCommentText("");
                }}
              >
                <i className="bi bi-arrow-left" aria-hidden />
                {t("app.appraisal.back", { defaultValue: "Back" })}
              </button>
            ) : (
              <Link
                className="appraisal-checkin-back"
                to={fromTeam ? "/appraisal/team-pdp" : "/appraisal/pdp"}
              >
                <i className="bi bi-arrow-left" aria-hidden />
                {t("app.appraisal.back", { defaultValue: "Back" })}
              </Link>
            )}
            <div className="appraisal-checkin-detail__head">
              <div>
                <h3>{task.activityName}</h3>
                <p className="appraisal-checkin-detail__meta">
                  {task.userName ? <span>{task.userName}</span> : null}
                  {task.designation ? <span>{task.designation}</span> : null}
                  {actor.isOwner ? <span>You (owner)</span> : null}
                  {actor.canManage ? <span>You (manager)</span> : null}
                </p>
              </div>
              <span
                className={`appraisal-status-pill appraisal-status-pill--pdp-${Number(task.status) || 0}`}
              >
                {pdpStatusLabel(task.status)}
              </span>
            </div>
            {task.description ? <p>{task.description}</p> : null}
            {task.learningMethod ? <p>Learning method: {task.learningMethod}</p> : null}
          </div>

          {canEdit && (
            <form className="appraisal-card" onSubmit={onSave}>
              <h4>Edit activity</h4>
              <label className="appraisal-field">
                Activity name
                <input
                  required
                  maxLength={APPRAISAL_LIMITS.headline}
                  value={form.activityName}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      activityName: clampLen(e.target.value, APPRAISAL_LIMITS.headline),
                    }))
                  }
                />
                <AppraisalCharCount value={form.activityName} max={APPRAISAL_LIMITS.headline} />
              </label>
              <label className="appraisal-field">
                Description
                <textarea
                  rows={3}
                  maxLength={APPRAISAL_LIMITS.longText}
                  value={form.description}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      description: clampLen(e.target.value, APPRAISAL_LIMITS.longText),
                    }))
                  }
                />
                <AppraisalCharCount value={form.description} max={APPRAISAL_LIMITS.longText} />
              </label>
              <button type="submit" className="appraisal-btn primary">
                Save changes
              </button>
            </form>
          )}

          <section className="appraisal-card appraisal-workflow-panel">
            <div className="appraisal-workflow-panel__head">
              <span className="appraisal-workflow-panel__icon" aria-hidden>
                <i className="bi bi-diagram-3" />
              </span>
              <div>
                <h4>Workflow</h4>
                <p className="appraisal-workflow-panel__hint">
                  Status: {pdpStatusLabel(task.status)}. Take the next step below.
                </p>
              </div>
              <span
                className={`appraisal-status-pill appraisal-status-pill--pdp-${Number(task.status) || 0}`}
              >
                {pdpStatusLabel(task.status)}
              </span>
            </div>
            <div className="appraisal-workflow-panel__body">
              {actor.isOwner && status === PDP_STATUS.Draft ? (
                <button
                  type="button"
                  className="appraisal-btn primary"
                  onClick={() => runAction(submitPdpForApproval)}
                >
                  Submit for approval
                </button>
              ) : null}
              {actor.isOwner && status === PDP_STATUS.ReadyForApproval ? (
                <p className="appraisal-framework-hint" style={{ margin: 0 }}>
                  Waiting for your manager to approve.
                </p>
              ) : null}
              {actor.canManage && status === PDP_STATUS.ReadyForApproval ? (
                <button type="button" className="appraisal-btn primary" onClick={() => runAction(approvePdp)}>
                  Approve (manager)
                </button>
              ) : null}
              {actor.canManage && status === PDP_STATUS.Draft ? (
                <p className="appraisal-framework-hint" style={{ margin: 0 }}>
                  Waiting for the employee to submit this activity.
                </p>
              ) : null}
              {(actor.isOwner || actor.canManage) && status === PDP_STATUS.Approved ? (
                <div style={{ width: "100%" }}>
                  <p className="appraisal-framework-hint" style={{ margin: "0 0 0.5rem" }}>
                    Activity is approved. Either the employee or the manager can mark it complete when the work is done.
                  </p>
                  <button type="button" className="appraisal-btn primary" onClick={() => runAction(completePdp)}>
                    Mark complete
                  </button>
                </div>
              ) : null}
              {status === PDP_STATUS.Completed ? (
                <p className="appraisal-framework-hint" style={{ margin: 0 }}>
                  This development activity is completed.
                </p>
              ) : null}
            </div>
          </section>

          {(actor.isOwner || actor.canManage) && (
            <section className="appraisal-card appraisal-comments-panel">
              <div className="appraisal-comments-panel__head">
                <span className="appraisal-comments-panel__icon" aria-hidden>
                  <i className="bi bi-chat-left-text" />
                </span>
                <div>
                  <h4>Comments</h4>
                  <p>Discuss progress, blockers, or next steps.</p>
                </div>
              </div>
              <div className="appraisal-comments-composer">
                <label className="appraisal-field">
                  Add comment
                  <textarea
                    rows={2}
                    maxLength={APPRAISAL_LIMITS.comment}
                    value={commentText}
                    onChange={(e) =>
                      setCommentText(clampLen(e.target.value, APPRAISAL_LIMITS.comment))
                    }
                    placeholder="Discuss progress, blockers, or next steps…"
                  />
                  <AppraisalCharCount value={commentText} max={APPRAISAL_LIMITS.comment} />
                </label>
                <button
                  type="button"
                  className="appraisal-btn"
                  disabled={!commentText.trim()}
                  onClick={() => runAction(addPdpComment, { commentText })}
                >
                  Post comment
                </button>
              </div>
              {task.comments?.length > 0 ? (
                <ul className="appraisal-checkin-comments">
                  {task.comments.map((c) => (
                    <li key={c.id || c.Id}>
                      <div className="appraisal-checkin-comments__head">
                        <strong>{c.userName || c.UserName || c.userId || c.UserId}</strong>
                        <span>
                          {(c.dateAdded || c.DateAdded)
                            ? new Date(c.dateAdded || c.DateAdded).toLocaleString()
                            : ""}
                        </span>
                      </div>
                      <p>{c.commentText || c.CommentText}</p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="appraisal-muted" style={{ marginTop: "1rem", marginBottom: 0 }}>
                  No comments yet.
                </p>
              )}
            </section>
          )}
        </>
      )}

      <AppraisalHelpModal helpKey={helpKey} onClose={() => setHelpKey(null)} />
    </div>
  );
};

export default AppraisalPdpPage;
